// Shared zod schemas. Centralised so backend validation contracts can grow without
// scattering rules across pages. Each schema mirrors the FastAPI Pydantic model on the
// server — but client-side validation lets us catch errors BEFORE the round-trip so
// users see inline feedback instead of a generic toast.

import { z } from "zod";

const requiredString = (label, min = 1, max = 200) =>
  z
    .string({ required_error: `${label} is required` })
    .trim()
    .min(min, `${label} is required`)
    .max(max, `${label} must be under ${max} characters`);

const emailField = z
  .string({ required_error: "Email is required" })
  .trim()
  .min(1, "Email is required")
  .email("That doesn't look like a valid email");

// -- Signup ----------------------------------------------------------------
export const signupSchema = z.object({
  business_name: requiredString("Business name", 2, 100),
  name: requiredString("Your name", 1, 100),
  email: emailField,
  password: z
    .string({ required_error: "Password is required" })
    .min(8, "Use at least 8 characters — strong passwords protect your data")
    .max(128, "Too long"),
  referral_code: z
    .string()
    .trim()
    .toUpperCase()
    .max(40, "Referral code too long")
    .optional()
    .or(z.literal("")),
});

// -- Invoice ---------------------------------------------------------------
// Mirrors the backend `InvoiceIn` Pydantic model. Catches client-side issues
// the backend would otherwise silently accept or 422 on.
const lineItemSchema = z.object({
  description: requiredString("Line item description", 1, 200),
  quantity: z.coerce
    .number({ invalid_type_error: "Quantity must be a number" })
    .positive("Quantity must be greater than zero")
    .max(999999, "Quantity is too large"),
  unit_price: z.coerce
    .number({ invalid_type_error: "Unit price must be a number" })
    .nonnegative("Unit price can't be negative")
    .max(9999999, "Unit price is too large"),
});

// Lightweight ISO-date check (YYYY-MM-DD). We're not pulling moment/dayjs just for this.
const isoDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");

export const invoiceSchema = z
  .object({
    invoice_number: requiredString("Invoice number", 1, 40),
    client_name: requiredString("Client name", 1, 200),
    client_email: z
      .string()
      .trim()
      .email("That doesn't look like a valid email")
      .or(z.literal(""))
      .optional(),
    line_items: z
      .array(lineItemSchema)
      .min(1, "Add at least one line item"),
    tax_rate: z.coerce
      .number({ invalid_type_error: "Tax rate must be a number" })
      .min(0, "Tax rate can't be negative")
      .max(100, "Tax rate can't exceed 100%"),
    currency: z.string().min(3).max(3).optional(),
    status: z.enum(["draft", "sent", "paid", "overdue"], {
      errorMap: () => ({ message: "Invalid status" }),
    }),
    issue_date: isoDate,
    due_date: isoDate,
    notes: z.string().max(2000, "Notes are too long").optional().or(z.literal("")),
  })
  .refine((d) => d.due_date >= d.issue_date, {
    message: "Due date must be on or after the issue date",
    path: ["due_date"],
  });
